import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@freighthub/silhouette-core'
import {
  fromQuoteSheetToFormState as fromQuoteSheet,
  getLocationCountryCode,
  IQuoteSheet,
  mapFormStateToPayload,
  QRCargo,
  QrCellCheckboxesInner,
  QRCustomsClearance,
  QuoteRequestFormState,
  useQuoteService,
} from 'features/BookingFlow'
import { FC } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { isEuropeCountryCode } from 'service/ISO'
import { useRootStore } from 'service/useRootStore'
import { I18N } from 'utils/localisation'
import { logError } from 'utils/logger'

import { IBookingTemplate } from '../BookingTemplate'
import { BookingTemplateBookingParty } from '../BookingTemplateDetail/BookingTemplateBookingParty'
import { BookingTemplateDetailRow } from '../BookingTemplateDetail/BookingTemplateDetailRow'
import { BookingTemplateIncoterm } from '../BookingTemplateDetail/BookingTemplateIncoterm'
import { BookingTemplateRequestInfo } from './BookingTemplateRequestInfo'
import { fromTemplate } from './fromBookingTemplate'

export type BookingTemplateRequestDialogProps = {
  template?: IBookingTemplate
  templateId: string
  quoteSheet?: IQuoteSheet
  onClose: () => void
}

const useStyles = makeStyles(() => ({
  label: {
    flexDirection: 'column',
  },
}))

export const BookingTemplateRequestDialog: FC<BookingTemplateRequestDialogProps> = ({
  onClose,
  quoteSheet,
  template,
}) => {
  const classes = useStyles()
  const { errorService } = useRootStore()
  const quoteService = useQuoteService()

  let defaultValues = {}
  if (quoteSheet) {
    defaultValues = fromQuoteSheet(quoteSheet)
  } else if (template) {
    defaultValues = fromTemplate(template)
  }

  const formMethods = useForm<QuoteRequestFormState>({
    defaultValues,
  })

  const origin = useWatch<QuoteRequestFormState, 'origin'>({ name: 'origin', control: formMethods.control })
  const hsCodesCount = useWatch<QuoteRequestFormState, 'hsCodesCount'>({
    name: 'hsCodesCount',
    control: formMethods.control,
  })
  const isOriginEUCountry = isEuropeCountryCode(getLocationCountryCode(origin) ?? '')

  const onSubmit = async (formState: QuoteRequestFormState) => {
    const newTemplate = mapFormStateToPayload(formState)
    try {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      const data = await quoteService.search(newTemplate, true, { fromTemplate: template._id })

      if (data && data.type === 'INVALID_REQUEST') {
        errorService.showDialog(data.message)
      }
    } catch (err) {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      logError(err)
    }
  }

  return (
    <FormProvider {...formMethods}>
      <Dialog open onClose={onClose} scroll="paper" maxWidth="md">
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <DialogTitle showClose onClose={onClose}>
            <I18N term="booking_templates-request_dialog-title" />
          </DialogTitle>
          <DialogContent>
            <Box p={2}>
              <BookingTemplateRequestInfo />
              <I18N term="booking_templates-request_dialog-content" />
              <BookingTemplateBookingParty />
              <BookingTemplateIncoterm />
              <BookingTemplateDetailRow labelTerm="quote_request-cargo-title" customClass={classes.label}>
                <QRCargo hideLabel />
              </BookingTemplateDetailRow>
              <QrCellCheckboxesInner>
                {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                {hsCodesCount > 0 && <QRCustomsClearance isOriginEUCountry={isOriginEUCountry} />}
              </QrCellCheckboxesInner>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>
              <I18N term="booking_templates-request_dialog-cancel" />
            </Button>
            <Button type="submit" variant="contained">
              <I18N term="booking_templates-request_dialog-search" />
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </FormProvider>
  )
}
